// Home: a working page with a docked panel (option A). On a first visit it
// explains the Console and lists the set-up steps; once the account has had an
// answer it shows what was spent, this session's answers and the next step.
// The lessons and use cases follow either way. See Design > Names and
// definitions, "The Console".

import { useState } from "preact/hooks";
import type { PillOption } from "../../../../ui/src/index.ts";
import { Button, DockPanel, FigureStrip, fmtNum, Icon, LinkButton, PageBar, PanelToggle, StatusBadge, Workspace } from "../../../../ui/src/index.ts";
import { shortHub } from "../../domain/answer.ts";
import { usd } from "../../domain/money.ts";
import { openCredits } from "../actions.ts";
import { LessonList, UseCaseList } from "../lessons.tsx";
import { AnswerDetail, HowItWorks, PanelHead, UseTheApi, type PanelTab } from "../panels.tsx";
import { href } from "../router.ts";
import { usePanelOpen, useSeen } from "../setup.ts";
import { useStore, type LedgerEntry } from "../store.ts";
import { ExamplesNote } from "../ui.tsx";

const CONCEPTS = [
  { term: "Hub", text: "A curated source of answers on one subject, run by a person who knows it." },
  { term: "Bid", text: "The most you'll pay for one answer. You pay the hub's price, never more." },
  { term: "Answer", text: "Data with the sources it came from. Only answers cost anything." },
  { term: "Refund", text: "Reject a wrong answer and its cost comes back at once." },
];

type Step = { id: string; title: string; text: string; done: boolean; verb: string; href?: string; onSelect?: () => void; unavailable?: string };

/** A step's verb: a link when it goes somewhere, a button when it opens something here. */
const StepButton = ({ step, solid }: { step: Step; solid: boolean }) => step.href
  ? <Button size="sm" variant={solid ? "solid" : "quiet"} href={step.href}>{step.verb}</Button>
  : <Button size="sm" variant={solid ? "solid" : "quiet"} onClick={step.onSelect}>{step.verb}</Button>;

function SetupList({ steps, next }: { steps: Step[]; next: Step | undefined }) {
  return <ol class="cs-setup">{steps.map((s) => <li key={s.id} data-done={s.done ? "" : undefined} data-next={s === next ? "" : undefined}>
    <Icon name={s.done ? "check" : "circle"} size={20} />
    <div><strong>{s.title}</strong><span>{s.text}</span></div>
    {s.done ? <span class="cs-muted">Done</span>
      : s.unavailable ? <Button size="sm" aria-disabled="true" title={s.unavailable} onClick={(e) => e.preventDefault()}>{s.verb}</Button>
      : <StepButton step={s} solid={s === next} />}
  </li>)}</ol>;
}

const time = (at: number) => new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function RecentAnswers({ rows, selected, onSelect }: { rows: LedgerEntry[]; selected: string | null; onSelect: (id: string) => void }) {
  const refunded = useStore((s) => s.refunded);
  return <div class="ui-table-wrap"><table class="ui-table cs-table">
    <caption class="ui-sr-only">This session's answers</caption>
    <thead><tr><th class="ui-table__fill">Question</th><th style={{ width: 180 }}>Hub</th><th style={{ width: 112 }}>Outcome</th><th class="ui-table__num" style={{ width: 96 }}>Cost</th><th style={{ width: 132 }}>When</th></tr></thead>
    <tbody>{rows.map((e) => <tr key={e.answerId} data-selected={e.answerId === selected ? "" : undefined} onClick={() => onSelect(e.answerId)}>
      <td class="ui-table__fill"><LinkButton truncate title={e.what} onClick={(ev) => { ev.stopPropagation(); onSelect(e.answerId); }}>{e.what}</LinkButton></td>
      <td class="mono cs-muted"><span class="cs-cell">{shortHub(e.hub)}</span></td>
      <td>{refunded.includes(e.answerId) ? <StatusBadge>Refunded</StatusBadge> : <StatusBadge tone="success">Answer</StatusBadge>}</td>
      <td class="ui-table__num mono">{usd(Math.abs(e.amount))}</td>
      <td class="cs-muted cs-nowrap">{time(e.at)}</td>
    </tr>)}</tbody>
  </table></div>;
}

export function Home() {
  const lessons = [...useStore((s) => s.content.lessons)].sort((a, b) => a.number - b.number);
  const useCases = useStore((s) => s.content.useCases);
  const examples = useStore((s) => s.examples);
  const ledger = useStore((s) => s.ledger);
  const account = useStore((s) => s.account);
  const accountId = useStore((s) => s.user?.account);
  const seenCredits = useSeen(accountId, "credits");
  const copied = useSeen(accountId, "copied");

  const answers = ledger.filter((e) => e.type === "Charge");
  const rows = account?.ledger ?? [];
  const answered = answers.length > 0 || rows.some((r) => r.kind === "charge");
  const first = !answered;

  const [open, setOpen] = usePanelOpen("home", true);
  const [tab, setTab] = useState<PanelTab>(answers.length ? "answer" : "how");
  const [picked, setPicked] = useState<string | null>(null);
  const entry = answers.find((e) => e.answerId === picked) ?? answers[0];
  const shown: PanelTab = tab === "answer" && !entry ? "how" : tab;
  const show = (t: PanelTab) => { setTab(t); setOpen(true); };

  const grant = rows.find((r) => r.kind === "grant")?.amount;
  const firstLesson = lessons[0];
  const steps: Step[] = [
    { id: "signin", title: "Sign in with WarmHub", text: grant ? `You're signed in, with ${usd(grant)} in starter credits.` : "You're signed in.", done: true, verb: "" },
    { id: "first", title: "Ask your first question", text: "A guided lesson in the Playground: pick a question, set a bid, send it.", done: answered,
      verb: firstLesson ? "Start lesson" : "Open the Playground", href: href(firstLesson ? `playground/${firstLesson.id}` : "playground") },
    { id: "credits", title: "See what you were charged", text: "Credits lists every charge and refund.", done: seenCredits, verb: "Open Credits", onSelect: openCredits },
    { id: "token", title: "Get your API token", text: "Your apps send your WarmHub token with each request.", done: false, verb: "Get token", unavailable: "Not connected in this prototype" },
    { id: "code", title: "Call the API from your code", text: "Copy a request as a curl call and run it.", done: copied, verb: "See a request", onSelect: () => show("api") },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done && !s.unavailable);

  // All-time numbers: the account's ledger as last read, plus this session's lines it doesn't have yet
  // (it's read at sign-in and when Credits opens, not after every request).
  const known = (kind: string) => new Set(rows.filter((r) => r.kind === kind).map((r) => r.answerId));
  const chargedIds = known("charge");
  const refundedIds = known("refund");
  const charges = [...rows.filter((r) => r.kind === "charge").map((r) => r.amount), ...answers.filter((e) => !chargedIds.has(e.answerId)).map((e) => e.amount)];
  const refunds = [...rows.filter((r) => r.kind === "refund").map((r) => r.amount), ...ledger.filter((e) => e.type === "Refund" && !refundedIds.has(e.answerId)).map((e) => e.amount)];
  const spent = -charges.reduce((sum, a) => sum + a, 0);
  const back = refunds.reduce((sum, a) => sum + a, 0);
  const scope = account ? "since you joined" : "this session";

  const tabs: PillOption<PanelTab>[] = [...(entry ? [{ value: "answer" as const, label: "Answer" }] : []), { value: "how", label: "How it works" }, { value: "api", label: "Use the API" }];
  const panel = open ? <DockPanel label="Details" head={<PanelHead tab={shown} tabs={tabs} onTab={setTab} onClose={() => setOpen(false)} />}>
    {shown === "answer" && entry ? <AnswerDetail entry={entry} /> : shown === "api" ? <UseTheApi /> : <HowItWorks />}
  </DockPanel> : undefined;

  const learn = <>
    <section class="cs-home-section">
      <div class="cs-home-section__head"><div><h2>Lessons</h2><p>Each one sends a real request and explains what came back.</p></div></div>
      {lessons.length === 0 ? <ExamplesNote examples={examples} /> : <LessonList lessons={lessons} />}
    </section>
    {useCases.length > 0 && <section class="cs-home-section">
      <div class="cs-home-section__head"><div><h2>What people ask</h2><p>Choose one to open it in the Playground with a bid that gets an answer.</p></div></div>
      <UseCaseList useCases={useCases} />
    </section>}
  </>;

  return <>
    <PageBar title="Home">
      <Button href="https://docs.warmhub.ai" target="_blank" rel="noreferrer">Docs</Button>
      <Button variant={first ? "quiet" : "solid"} href={href("playground")}><Icon name="plus" size={16} />New request</Button>
      <PanelToggle open={open} onToggle={() => setOpen(!open)} />
    </PageBar>
    <Workspace panel={panel}>
      {first ? <>
        <div class="cs-intro">
          <h2>Welcome to the Console</h2>
          <p>Ask a question in plain words. Hubs that know the subject answer from their curated data, with the sources they used, and you pay only for answers. Here you try it, see what it costs, and take it into your code.</p>
        </div>
        <dl class="cs-concepts">{CONCEPTS.map((c) => <div key={c.term}><dt>{c.term}</dt><dd>{c.text}</dd></div>)}</dl>
        <section class="cs-home-section">
          <div class="cs-home-section__head"><div><h2>Get set up</h2><p>{doneCount} of {steps.length} done. Each step takes a minute or two.</p></div></div>
          <SetupList steps={steps} next={next} />
        </section>
        {learn}
      </> : <>
        <FigureStrip label="Your account" figures={[
          { label: "Spent", value: usd(spent), context: scope, action: { label: "Credits", onSelect: openCredits } },
          { label: "Answers", value: fmtNum(charges.length), context: `${fmtNum(answers.length)} this session`, action: { label: "Usage", href: href("usage") } },
          { label: "Refunded", value: usd(back), context: refunds.length === 1 ? "1 rejected answer" : `${fmtNum(refunds.length)} rejected answers`, action: { label: "Credits", onSelect: openCredits } },
        ]} />
        {next && <div class="cs-nextstep">
          <span><strong>Next step: {next.title.toLowerCase()}.</strong> {next.text}</span>
          <span class="cs-muted">{doneCount} of {steps.length} set up</span>
          <StepButton step={next} solid={false} />
        </div>}
        <section class="cs-home-section">
          <div class="cs-home-section__head"><div><h2>This session's answers</h2><p>Select one to see what it cost and to ask it again.</p></div></div>
          {answers.length > 0
            ? <RecentAnswers rows={answers} selected={shown === "answer" ? entry?.answerId ?? null : null} onSelect={(id) => { setPicked(id); show("answer"); }} />
            : <div class="cs-empty"><p>Answers you get in this session show here. Every earlier charge is in Credits.</p><Button size="sm" onClick={openCredits}>Open Credits</Button></div>}
        </section>
        {learn}
      </>}
    </Workspace>
  </>;
}
